import './widget.css';

import { FieldConfig, useField } from 'formik';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import {
  NumericInput,
  NumericInputRef,
} from '../../../../../components/inputs/numeric-input';
import { IonItem } from '../../../../../components/ionic/IonItem';
import { useWidgetClasses } from '../../hook/widget.hooks';
import { getNumericFormatSuffix } from '../../util/widget';
import { WidgetLabel } from './widget-label';
import {
  NumericFormat,
  NumericWidgetProps,
  WidgetRef,
  createWidgetExtension,
} from './widget.types';

interface NumericInputWidgetProps extends NumericWidgetProps {
  format?: NumericFormat;
  requireNegative?: boolean;
}

const NumericInputWidget = (
  props: NumericInputWidgetProps & FieldConfig
): ReactElement => {
  const {
    label,
    description,
    ionItemProps,
    extensions,
    disabled,
    showDescription,
    step,
    subtitle,
    subtitleColor,
    format,
    isNested,
    isPaywalled,
    transparentBackground,
    requireNegative,
    ...otherProps
  } = props;
  const { className: ionItemClassName, ...restIonItemProps } =
    ionItemProps ?? {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [field, meta, helper] = useField(otherProps as any);
  const inputRef = useRef<NumericInputRef>(null);
  const [isDisabled, setIsDisabled] = useState(disabled ?? false);

  const rightElement = useMemo(() => {
    return createWidgetExtension(extensions?.rightElement, {
      field: [field, meta, helper],
      widgetRef: inputRef.current as WidgetRef,
      setDisabled: setIsDisabled,
      isDisabled,
    });
  }, [extensions?.rightElement, field, helper, isDisabled, meta]);

  useEffect(() => {
    if (disabled !== undefined) {
      setIsDisabled(disabled);
    }
  }, [disabled]);

  const classList = useWidgetClasses({
    isNested,
    transparentBackground,
    isPaywalled,
  });

  const ionLabelClass = isNested ? 'ion-padding-start' : undefined;

  return (
    <IonItem
      className={`${classList} ${ionItemClassName || ''}`.trim()}
      lines="full"
      disabled={isDisabled}
      onClick={async () => {
        const nativeInput = await inputRef.current?.getInputElement();
        nativeInput?.select();
      }}
      {...restIonItemProps}
      onTouchStart={(e) => {
        inputRef.current?.inputFocus(e);
      }}
      onTouchEnd={(e) => {
        inputRef.current?.inputFocus(e);
      }}
    >
      <NumericInput
        ref={inputRef}
        requireNegative={requireNegative}
        ionInputProps={{
          step: step ? `${step}` : undefined,
          disabled: isDisabled,
          value: field.value,
          onIonInput: (e) => {
            if (isPaywalled) {
              return;
            }

            helper.setValue(e.detail.value);
          },
          'aria-label': label,
          'data-cy': otherProps.name,
        }}
      >
        <div slot="label">
          <WidgetLabel
            label={label}
            description={description}
            showDescription={showDescription}
            error={meta.error}
            forceDisable={isDisabled}
            subtitle={subtitle}
            subtitleColor={subtitleColor}
            ionLabelProps={{
              className: ionLabelClass,
            }}
          />
        </div>
        {rightElement}
      </NumericInput>
      {format && getNumericFormatSuffix(format)}
    </IonItem>
  );
};

export { NumericInputWidget };
